import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Palette, UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_CURRENT_COMPANY_INFO } from "../GetCurrentCompanyInfo/useGetCurrentCompanyInfo";

import { companiesApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_COMPANY_PALETTE = "UPDATE_COMPANY_PALETTE";

const allowedRoles = [UserRole.Owner];

interface UpdateCompanyPaletteRequest {
  companyId: string;
  color: Palette;
}

const updateCompanyPalette = async (
  params: UpdateCompanyPaletteRequest,
): Promise<void> => {
  const { companyId, color } = params;

  await companiesApi.companiesControllerUpdateCompanyPalette(companyId, {
    palette: color,
  });
};

export const useUpdateCompanyPalette = (
  user: UserProfileDto | null,
): {
  updateCompanyPalette: (params: UpdateCompanyPaletteRequest) => void;
  updateCompanyPaletteEnabled: boolean;
  isSuccessUpdateCompanyPalette: boolean;
  isPendingUpdateCompanyPalette: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isPending } = useMutation<
    void,
    Error,
    UpdateCompanyPaletteRequest
  >({
    mutationKey: [DOMAIN_COMPANIES, ACTION_UPDATE_COMPANY_PALETTE],
    mutationFn: enabled ? updateCompanyPalette : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_CURRENT_COMPANY_INFO],
      });
    },
  });

  return {
    updateCompanyPalette: mutate,
    updateCompanyPaletteEnabled: enabled,
    isSuccessUpdateCompanyPalette: isSuccess,
    isPendingUpdateCompanyPalette: isPending,
  };
};
